import { ErrorMessage, Field, Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Button } from "reactstrap";
import * as Yup from "yup";
import { getProfile, updateProfile } from "../../../../api/settings";
import {
  Block,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Col,
  PreviewAltCard,
  Row,
} from "../../../../components/Component";
import Content from "../../../../layout/content/Content";
import Head from "../../../../layout/head/Head";

const Settings = () => {
  const [initialValues, setInitialValues] = useState(null);
  const [loading, setLoading] = useState(true); // Added loading state

  const validationSchema = Yup.object({
    username: Yup.string().required("Username is required"),
    email: Yup.string().email("Invalid email format").required("Email is required"),
    phone: Yup.string()
      .matches(/^\+?[0-9]{10,15}$/, "Phone number is not valid")
      .required("Phone number is required"),
    shop_name: Yup.string().required("Shop name is required"),
    street: Yup.string().required("Street address is required"),
    city: Yup.string().required("City is required"),
    state: Yup.string().required("State is required"),
    postalCode: Yup.string()
      .matches(/^[0-9]{6}$/, "Postal code must be 6 digits")
      .required("Postal code is required"),
    country: Yup.string().required("Country is required"),
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true); // Set loading to true before fetching
        const response = await getProfile();
        if (response.data.success) {
          const data = response.data.data;
          setInitialValues({
            username: data.username,
            email: data.email,
            phone: data.phone,
            shop_name: data.shop_name,
            street: data.shop_address.street,
            city: data.shop_address.city,
            state: data.shop_address.state,
            postalCode: data.shop_address.postalCode,
            country: data.shop_address.country,
          });
        } else {
          toast.error("Failed to fetch profile data");
        }
      } catch (error) {
        toast.error("Error fetching profile: " + error.message);
      } finally {
        setLoading(false); // Set loading to false after fetching
      }
    };
    fetchProfile();
  }, []);

  const handleSubmit = async (values) => {
    try {
      const response = await updateProfile(values);
      if (response.data.success) {
        toast.success("Profile updated successfully!");
      } else {
        toast.error("Failed to update profile");
      }
    } catch (error) {
      toast.error("Error updating profile: " + (error.response?.message || error.message));
    }
  };

  if (loading) {
    return (
      <React.Fragment>
        <Head title="Settings"></Head>
        <Content>
          <BlockHead size="sm">
            <BlockHeadContent>
              <BlockTitle page>Settings</BlockTitle>
            </BlockHeadContent>
          </BlockHead>
          <Block>
            <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
              <div className="text-center">
                <div
                  className="spinner-grow text-primary"
                  style={{ width: "3rem", height: "3rem" }}
                  role="status"
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3 text-muted">Loading profile details, please wait...</p>
              </div>
            </div>
          </Block>
        </Content>
      </React.Fragment>
    );
  }

  if (!initialValues) {
    return (
      <React.Fragment>
        <Head title="Settings"></Head>
        <Content>
          <BlockHead size="sm">
            <BlockHeadContent>
              <BlockTitle page>Settings</BlockTitle>
            </BlockHeadContent>
          </BlockHead>
          <Block>
            <p>Failed to load profile data.</p>
          </Block>
        </Content>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Head title="Settings"></Head>
      <Content>
        <BlockHead size="sm">
          <BlockHeadContent>
            <BlockTitle page>Settings</BlockTitle>
          </BlockHeadContent>
        </BlockHead>

        <Block>
          <PreviewAltCard>
            <h5 className="card-title">Supplier Profile</h5>
            <p>View and update your supplier profile details below.</p>

            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting }) => (
                <Form className="gy-3 form-settings">
                  <Row className="g-3 align-center">
                    <Col lg="5">
                      <div className="form-group">
                        <label className="form-label" htmlFor="username">Username</label>
                        <span className="form-note">Specify your username.</span>
                      </div>
                    </Col>
                    <Col lg="7">
                      <div className="form-group">
                        <Field type="text" className="form-control" id="username" name="username" />
                        <ErrorMessage name="username" component="div" className="text-danger" />
                      </div>
                    </Col>
                  </Row>

                  <Row className="g-3 align-center">
                    <Col lg="5">
                      <div className="form-group">
                        <label className="form-label" htmlFor="email">Email</label>
                        <span className="form-note">Specify your email address.</span>
                      </div>
                    </Col>
                    <Col lg="7">
                      <div className="form-group">
                        <Field type="email" className="form-control" id="email" name="email" />
                        <ErrorMessage name="email" component="div" className="text-danger" />
                      </div>
                    </Col>
                  </Row>

                  <Row className="g-3 align-center">
                    <Col lg="5">
                      <div className="form-group">
                        <label className="form-label" htmlFor="phone">Phone</label>
                        <span className="form-note">Specify your phone number.</span>
                      </div>
                    </Col>
                    <Col lg="7">
                      <div className="form-group">
                        <Field type="text" className="form-control" id="phone" name="phone" />
                        <ErrorMessage name="phone" component="div" className="text-danger" />
                      </div>
                    </Col>
                  </Row>

                  <Row className="g-3 align-center">
                    <Col lg="5">
                      <div className="form-group">
                        <label className="form-label" htmlFor="shop_name">Shop Name</label>
                        <span className="form-note">Specify the name of your shop.</span>
                      </div>
                    </Col>
                    <Col lg="7">
                      <div className="form-group">
                        <Field type="text" className="form-control" id="shop_name" name="shop_name" />
                        <ErrorMessage name="shop_name" component="div" className="text-danger" />
                      </div>
                    </Col>
                  </Row>

                  <Row className="g-3 align-center">
                    <Col lg="5">
                      <div className="form-group">
                        <label className="form-label" htmlFor="street">Street</label>
                        <span className="form-note">Specify your street address.</span>
                      </div>
                    </Col>
                    <Col lg="7">
                      <div className="form-group">
                        <Field type="text" className="form-control" id="street" name="street" />
                        <ErrorMessage name="street" component="div" className="text-danger" />
                      </div>
                    </Col>
                  </Row>

                  <Row className="g-3 align-center">
                    <Col lg="5">
                      <div className="form-group">
                        <label className="form-label" htmlFor="city">City</label>
                        <span className="form-note">Specify your city.</span>
                      </div>
                    </Col>
                    <Col lg="7">
                      <div className="form-group">
                        <Field type="text" className="form-control" id="city" name="city" />
                        <ErrorMessage name="city" component="div" className="text-danger" />
                      </div>
                    </Col>
                  </Row>

                  <Row className="g-3 align-center">
                    <Col lg="5">
                      <div className="form-group">
                        <label className="form-label" htmlFor="state">State</label>
                        <span className="form-note">Specify your state.</span>
                      </div>
                    </Col>
                    <Col lg="7">
                      <div className="form-group">
                        <Field type="text" className="form-control" id="state" name="state" />
                        <ErrorMessage name="state" component="div" className="text-danger" />
                      </div>
                    </Col>
                  </Row>

                  <Row className="g-3 align-center">
                    <Col lg="5">
                      <div className="form-group">
                        <label className="form-label" htmlFor="postalCode">Postal Code</label>
                        <span className="form-note">Specify your postal code.</span>
                      </div>
                    </Col>
                    <Col lg="7">
                      <div className="form-group">
                        <Field type="text" className="form-control" id="postalCode" name="postalCode" />
                        <ErrorMessage name="postalCode" component="div" className="text-danger" />
                      </div>
                    </Col>
                  </Row>

                  <Row className="g-3 align-center">
                    <Col lg="5">
                      <div className="form-group">
                        <label className="form-label" htmlFor="country">Country</label>
                        <span className="form-note">Specify your country.</span>
                      </div>
                    </Col>
                    <Col lg="7">
                      <div className="form-group">
                        <Field type="text" className="form-control" id="country" name="country" />
                        <ErrorMessage name="country" component="div" className="text-danger" />
                      </div>
                    </Col>
                  </Row>

                  <Row className="g-3">
                    <Col lg="5" className="offset-lg-5">
                      <div className="form-group mt-2">
                        <Button size="lg" color="primary" type="submit" disabled={isSubmitting}>
                          Update Profile
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Form>
              )}
            </Formik>
          </PreviewAltCard>
        </Block>
      </Content>
    </React.Fragment>
  );
};

export default Settings;